import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost } from "../../services/api";
import type { Product, ProductCategory, Location, CartItem, POSConfig } from "../../types";
import POSSettings from "./POSSettings";
import RefundModal from "./RefundModal";
import CustomItemModal from "./CustomItemModal";
import "../../styles/pos.css";

const DEFAULT_POS_CONFIG: POSConfig = {
  organizationId: "",
  taxRules: [{ id: "state", name: "State Sales Tax", rate: 0.07, type: "state", enabled: true }],
  paymentMethods: { cash: true, card: true, giftCard: false, other: false },
  receiptSettings: { showLogo: true, headerText: "", footerText: "Thank you!", autoPrint: false },
  terminalSettings: { requireLocation: true, allowDiscounts: false, allowVoid: true, defaultTaxEnabled: true },
};

export default function POSTerminal() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [posConfig, setPosConfig] = useState<POSConfig>(DEFAULT_POS_CONFIG);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [locationId, setLocationId] = useState("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [lastTransaction, setLastTransaction] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [showCustomItem, setShowCustomItem] = useState(false);
  const [serialPrompt, setSerialPrompt] = useState<Product | null>(null);

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [prods, cats, locs, config] = await Promise.all([
        apiGet<Product[]>(`/organizations/${orgId}/products`),
        apiGet<ProductCategory[]>(`/organizations/${orgId}/categories`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
        apiGet<POSConfig>(`/organizations/${orgId}/pos-config`).catch(() => null),
      ]);
      setProducts(prods.filter((p: any) => p.status === "active"));
      setCategories(cats);
      setLocations(locs);
      if (config && config.taxRules) setPosConfig(config);
      if (locs.length === 1) setLocationId((locs[0] as any).locationId);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const totalTaxRate = useMemo(() => {
    if (!posConfig.terminalSettings.defaultTaxEnabled) return 0;
    return posConfig.taxRules.filter((r) => r.enabled).reduce((sum, r) => sum + r.rate, 0);
  }, [posConfig]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const q = search.toLowerCase();
      const matchSearch = !search || p.name.toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.barcode || "").includes(search);
      const matchCat = !categoryFilter || p.categoryId === categoryFilter;
      return matchSearch && matchCat;
    });
  }, [products, search, categoryFilter]);

  const addCustomItem = (item: { name: string; price: number; taxable: boolean }) => {
    const customId = `custom-${Date.now()}`;
    setCart((prev) => [...prev, { productId: customId, name: item.name, price: item.price, quantity: 1, taxable: item.taxable, isCustomItem: true }]);
    setLastTransaction(null);
  };


  const addToCart = (product: Product) => {
    // If product requires serial number, prompt first
    if (product.requiresSerialNumber) {
      setSerialPrompt(product);
      return;
    }
    addProductToCart(product);
  };

  const addProductToCart = (product: Product, serialNumber?: string) => {
    setCart((prev) => {
      // Serial number items are always added as new line items (each unit has unique SN)
      if (serialNumber) {
        return [...prev, { productId: product.productId, name: product.name, price: product.price, quantity: 1, taxable: product.taxable, serialNumber }];
      }
      const existing = prev.find((i) => i.productId === product.productId && !i.serialNumber);
      if (existing) return prev.map((i) => i.productId === product.productId && !i.serialNumber ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { productId: product.productId, name: product.name, price: product.price, quantity: 1, taxable: product.taxable }];
    });
    setLastTransaction(null);
  };

  const updateQty = (productId: string, delta: number) => {
    setCart((prev) => prev.map((i) => i.productId === productId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i).filter((i) => i.quantity > 0));
  };

  const subtotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const taxableAmount = cart.filter((i) => i.taxable).reduce((sum, i) => sum + i.price * i.quantity, 0);
  const tax = taxableAmount * totalTaxRate;
  const total = subtotal + tax;

  const isDisabled = cart.length === 0 || (posConfig.terminalSettings.requireLocation && !locationId) || processing;

  const checkout = async (paymentMethod: string) => {
    if (isDisabled) return;
    setProcessing(true);
    try {
      await apiPost(`/organizations/${orgId}/transactions`, {
        locationId, employeeId: user?.id || "", items: cart,
        subtotal: Math.round(subtotal * 100) / 100,
        tax: Math.round(tax * 100) / 100,
        taxRate: totalTaxRate,
        total: Math.round(total * 100) / 100,
        paymentMethod, status: "completed",
      });
      setLastTransaction(`${paymentMethod.toUpperCase()} — $${total.toFixed(2)}`);
      setCart([]);
    } catch (err) { console.error(err); }
    finally { setProcessing(false); }
  };

  if (loading) return <div className="empty-state">Loading POS...</div>;

  return (
    <>
      <div className="pos-layout">
        <div className="pos-products">
          <div className="pos-location-bar">
            <span>📍 Location:</span>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">Select location...</option>
              {locations.map((l: any) => <option key={l.locationId} value={l.locationId}>{l.name} ({l.storeNumber})</option>)}
            </select>
            <div style={{ marginLeft: "auto" }}>
              <button onClick={() => setShowRefund(true)} style={{ background: "none", border: "1px solid #dc2626", color: "#dc2626", borderRadius: "4px", padding: "0.3rem 0.6rem", cursor: "pointer", fontSize: "0.8rem", marginRight: "0.5rem" }}>↩️ Refund</button><button onClick={() => setShowSettings(true)} style={{ background: "none", border: "1px solid #d1d5db", borderRadius: "4px", padding: "0.3rem 0.6rem", cursor: "pointer", fontSize: "0.8rem" }}>⚙️ Settings</button>
            </div>
          </div>
          <div className="pos-search" style={{ flexWrap: "wrap" }}>
            <input placeholder="Search products or scan barcode..." value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">All</option>
              {categories.map((c: any) => <option key={c.categoryId} value={c.categoryId}>{c.name}</option>)}
            </select>
            <button type="button" onClick={() => setShowCustomItem(true)} style={{ padding: "0.6rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.85rem", background: "white", cursor: "pointer", whiteSpace: "nowrap" }}>+ Custom</button>
          </div>
          <div className="pos-grid">
            {filteredProducts.map((p) => (
              <div key={p.productId} className="pos-item" onClick={() => addToCart(p)}>
                <div className="item-name">{p.name}</div>
                <div className="item-price">${p.price.toFixed(2)}</div>
              </div>
            ))}
            {filteredProducts.length === 0 && (
              <div style={{ gridColumn: "1/-1", textAlign: "center", color: "#9ca3af", padding: "3rem" }}>
                {products.length === 0 ? "No products. Add products in Inventory first." : "No matches."}
              </div>
            )}
          </div>
        </div>

        <div className="pos-cart">
          <div className="pos-cart-header">Cart ({cart.reduce((s, i) => s + i.quantity, 0)} items)</div>
          {cart.length === 0 ? (
            <div className="pos-cart-empty">{lastTransaction ? `✅ ${lastTransaction}` : "Tap products to add"}</div>
          ) : (
            <div className="pos-cart-items">
              {cart.map((item) => (
                <div key={item.productId} className="pos-cart-item">
                  <div className="cart-item-info">
                    <div className="cart-item-name">{item.name}</div>
                    <div className="cart-item-price">${item.price.toFixed(2)} ea</div>
                  </div>
                  <div className="cart-item-qty">
                    <button onClick={() => updateQty(item.productId, -1)}>−</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => updateQty(item.productId, 1)}>+</button>
                  </div>
                  <div className="cart-item-total">${(item.price * item.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="pos-cart-footer">
            <div className="pos-totals">
              <div className="total-row"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="total-row"><span>Tax ({(totalTaxRate * 100).toFixed(2)}%)</span><span>${tax.toFixed(2)}</span></div>
              <div className="total-row grand"><span>Total</span><span>${total.toFixed(2)}</span></div>
            </div>
            <div className="pos-pay-buttons">
              {posConfig.paymentMethods.cash && (
                <button className="pay-cash" disabled={isDisabled} onClick={() => checkout("cash")}>💵 Cash</button>
              )}
              {posConfig.paymentMethods.card && (
                <button className="pay-card" disabled={isDisabled} onClick={() => checkout("card")}>💳 Card</button>
              )}
              {posConfig.paymentMethods.giftCard && (
                <button disabled={isDisabled} onClick={() => checkout("gift-card")} style={{ background: "#7c3aed", color: "white", padding: "0.75rem", border: "none", borderRadius: "6px", fontSize: "0.9rem", fontWeight: 600, cursor: "pointer" }}>🎁 Gift Card</button>
              )}
              {posConfig.paymentMethods.other && (
                <button disabled={isDisabled} onClick={() => checkout("other")} style={{ background: "#6b7280", color: "white", padding: "0.75rem", border: "none", borderRadius: "6px", fontSize: "0.9rem", fontWeight: 600, cursor: "pointer" }}>📝 Other</button>
              )}
            </div>
            {cart.length > 0 && posConfig.terminalSettings.allowVoid && (
              <button onClick={() => setCart([])} style={{ marginTop: "0.5rem", width: "100%", padding: "0.5rem", background: "none", border: "1px solid #dc2626", color: "#dc2626", borderRadius: "6px", fontSize: "0.85rem", cursor: "pointer" }}>
                🚫 Void Cart
              </button>
            )}
          </div>
        </div>
      </div>
      {showSettings && <POSSettings onClose={() => { setShowSettings(false); loadData(); }} />}
      {showRefund && <RefundModal onClose={() => setShowRefund(false)} />}
      {showCustomItem && <CustomItemModal onAdd={addCustomItem} onClose={() => setShowCustomItem(false)} />}
      {serialPrompt && (
        <div className="modal-overlay" onClick={() => setSerialPrompt(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "380px" }}>
            <h3>Serial Number Required</h3>
            <p style={{ color: "#6b7280", fontSize: "0.85rem", marginBottom: "1rem" }}>Scan or enter the serial number for <strong>{serialPrompt.name}</strong></p>
            <form onSubmit={(e) => { e.preventDefault(); const input = (e.target as any).elements.sn; if (input.value.trim()) { addProductToCart(serialPrompt, input.value.trim()); setSerialPrompt(null); } }}>
              <input name="sn" placeholder="Scan serial number..." autoFocus style={{ width: "100%", padding: "0.7rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "1rem", fontFamily: "monospace", marginBottom: "1rem" }} />
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setSerialPrompt(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ width: "auto" }}>Add to Cart</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
